import { Box, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { RowComponentProps, List as VirtualizedList } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

import { AssignableRecipient, User, UserMin } from '@apiContract';

import { getRecipientId } from '@utils/groups';

import { sxList, sxNoResult } from '../../styles';
import Item from './Item';

type ListProps = {
  loadNextPage: (startIndex: number, stopIndex: number) => void | Promise<void>;
  isNextPageLoading: boolean;
  items: Array<AssignableRecipient>;
  setSelectedUser: (user: AssignableRecipient) => void;
  selectedUser?: AssignableRecipient | User | UserMin | null;
  hasNextPage: boolean;
  changeTab?: () => void;
  darkMode?: boolean;
  isForward?: boolean;
};

const UserItem = ({
  style,
  index,
  changeTab,
  selectedUser,
  items,
  setSelectedUser,
  isNextPageLoading,
  isForward,
}: RowComponentProps<{
  changeTab?: () => void;
  selectedUser?: AssignableRecipient | User | UserMin | null;
  items: Array<AssignableRecipient>;
  setSelectedUser: (user: AssignableRecipient) => void;
  isNextPageLoading: boolean;
  isForward?: boolean;
}>) => (
  <>
    <Item
      changeTab={changeTab}
      style={style}
      selected={!!selectedUser && !!items[index] && getRecipientId(items[index]) === getRecipientId(selectedUser)}
      item={items[index]}
      handleClick={() => items[index] && setSelectedUser(items[index])}
      isForward={isForward}
    />
    {items.length === 0 && !isNextPageLoading ? (
      <Typography sx={sxNoResult} variant="body2">
        No results found
      </Typography>
    ) : null}
  </>
);

const List: FC<ListProps> = ({
  loadNextPage,
  isNextPageLoading,
  items,
  setSelectedUser,
  selectedUser,
  hasNextPage,
  changeTab,
  darkMode = false,
  isForward = false,
}) => {
  const theme = useTheme();

  const rowCount = hasNextPage ? items.length + 1 : items.length + 1;
  const loadMoreRows = isNextPageLoading ? () => {} : loadNextPage;
  const isRowLoaded = (index: number) => !hasNextPage || index < items.length;
  const onRowsRendered = useInfiniteLoader({
    isRowLoaded,
    loadMoreRows: loadMoreRows as unknown as (startIndex: number, stopIndex: number) => Promise<void>,
    rowCount,
    threshold: 50,
  });

  return (
    <Box sx={sxList(theme, darkMode)}>
      <AutoSizer>
        {({ height, width }) => {
          return (
            <VirtualizedList
              style={{ height, width, maxHeight: 'inherit' }}
              onRowsRendered={onRowsRendered}
              rowCount={rowCount}
              rowHeight={60}
              rowProps={{
                changeTab,
                selectedUser,
                items,
                setSelectedUser,
                isNextPageLoading,
                isForward,
              }}
              className="customScrollBar"
              rowComponent={UserItem}
            />
          );
        }}
      </AutoSizer>
    </Box>
  );
};

export default List;
